import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Search, MessageCircle, Globe, Zap, TrendingUp } from "lucide-react";
import "./sourceBadge.css";

// Map raw sourceType → friendly label + icon + accent color
const SOURCE_META = {
  facebook: { label: "Facebook", Icon: Facebook, color: "#1877F2" },
  fb: { label: "Facebook", Icon: Facebook, color: "#1877F2" },
  instagram: { label: "Instagram", Icon: Instagram, color: "#E1306C" },
  ig: { label: "Instagram", Icon: Instagram, color: "#E1306C" },
  google: { label: "Google", Icon: Search, color: "#4285F4" },
  adwords: { label: "Google", Icon: Search, color: "#4285F4" },
  ctwa: { label: "WA Ad", Icon: Zap, color: "#25D366" },
  whatsapp_ad: { label: "WA Ad", Icon: Zap, color: "#25D366" },
  wa: { label: "Direct WA", Icon: MessageCircle, color: "#25D366" },
  direct: { label: "Direct WA", Icon: MessageCircle, color: "#25D366" },
  website: { label: "Website", Icon: Globe, color: "#8b8f9a" },
  organic: { label: "Organic", Icon: TrendingUp, color: "#8b8f9a" },
  "": { label: "Organic", Icon: TrendingUp, color: "#8b8f9a" },
};

export function resolveSource(lead) {
  const src = (lead?.leadSource?.sourceType || "").toLowerCase();
  // CTWA clid present = paid ad attribution confirmed
  if (lead?.leadSource?.ctwaClid) {
    return { ...SOURCE_META.ctwa, label: "WA Ad ✓" };
  }
  return SOURCE_META[src] || SOURCE_META.organic;
}

export function SourceBadge({ lead, size = "sm" }) {
  const meta = resolveSource(lead);
  const { Icon, label, color } = meta;
  return (
    <span
      className={`source-badge source-badge-${size}`}
      style={{ "--src-color": color }}
      data-testid={`source-badge-${lead?.id || ""}`}
      title={label}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ── Leads-by-source widget (Today dashboard) ─────
export function LeadsBySourceWidget({ apiBase }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${apiBase}/api/demo/analytics/leads-by-source?days=${days}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .finally(() => setLoading(false));
  }, [apiBase, days]);

  if (!data) {
    return (
      <div className="lbs-widget lbs-loading" data-testid="leads-by-source-loading">
        Loading lead sources...
      </div>
    );
  }

  const maxCount = Math.max(1, ...(data.rows || []).map((r) => r.total));
  const maxDaily = Math.max(1, ...(data.daily || []).map((d) => d.total));

  return (
    <div className="lbs-widget" data-testid="leads-by-source-widget">
      <div className="lbs-head">
        <div className="lbs-title">
          <TrendingUp className="w-4 h-4" />
          <strong>Leads by source</strong>
          <span className="lbs-sub">last {days} days</span>
        </div>
        <div className="lbs-range">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              className={`lbs-range-btn ${days === d ? "lbs-range-active" : ""}`}
              onClick={() => setDays(d)}
              data-testid={`lbs-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Top totals */}
      <div className="lbs-totals">
        <div className="lbs-total-card">
          <span className="lbs-total-num" data-testid="lbs-total-leads">{data.totals?.leads || 0}</span>
          <span className="lbs-total-label">Leads</span>
        </div>
        <div className="lbs-total-card">
          <span className="lbs-total-num">{data.totals?.booked || 0}</span>
          <span className="lbs-total-label">Demos booked</span>
        </div>
        <div className="lbs-total-card">
          <span className="lbs-total-num lbs-total-win">{data.totals?.converted || 0}</span>
          <span className="lbs-total-label">Paid 🎉</span>
        </div>
      </div>

      {/* Source breakdown bars */}
      {(data.rows || []).length === 0 ? (
        <div className="lbs-empty">No leads in this period yet — run an ad campaign to start seeing data here.</div>
      ) : (
        <div className="lbs-rows">
          {(data.rows || []).map((r) => {
            const meta = resolveSource({ leadSource: { sourceType: r.sourceType, ctwaClid: r.ctwa_count ? "x" : null } });
            const { Icon, label, color } = meta;
            const pct = Math.round((r.total / maxCount) * 100);
            return (
              <div key={r.sourceType + r.label} className="lbs-row" data-testid={`lbs-row-${r.sourceType || 'organic'}`}>
                <div className="lbs-row-label">
                  <span className="lbs-row-icon" style={{ background: color + "22", color }}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="lbs-row-name">{label}</span>
                </div>
                <div className="lbs-row-bar-wrap">
                  <div className="lbs-row-bar" style={{ width: `${pct}%`, background: color }} />
                </div>
                <div className="lbs-row-stats">
                  <span className="lbs-stat-leads">{r.total} leads</span>
                  <span className="lbs-stat-rate" title="Booking rate">{r.book_rate}% book</span>
                  <span className="lbs-stat-rate lbs-stat-conv" title="Conversion rate">{r.conv_rate}% paid</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mini daily spark bars */}
      {(data.daily || []).length > 1 && (
        <div className="lbs-daily">
          {data.daily.map((d, i) => (
            <div key={i} className="lbs-day" title={`${d.date}: ${d.total} leads`}>
              <div className="lbs-day-bar" style={{ height: `${Math.max(2, (d.total / maxDaily) * 100)}%` }} />
              <span className="lbs-day-label">{d.date.split(" ")[1]}</span>
            </div>
          ))}
        </div>
      )}
      {loading && <div className="lbs-loading-overlay">Refreshing...</div>}
    </div>
  );
}
